
import { MotionData, InteractionMode } from '../types';

export class MotionService {
  private lastX: number = 0;
  private lastY: number = 0;
  private lastZ: number = 0;
  private listeners: ((data: MotionData) => void)[] = [];
  private isListening: boolean = false;
  private isDesktop: boolean = false;
  private mode: InteractionMode = InteractionMode.MOTION;
  private sensitivity: number = 1.0;

  constructor() {
    this.isDesktop = !(/Mobi|Android|iPhone|iPad/i.test(navigator.userAgent));
  }

  public setSensitivity(s: number) {
    this.sensitivity = Math.max(0.2, Math.min(3.0, s));
  }

  public getSensitivity(): number {
    return this.sensitivity;
  }

  public setMode(mode: InteractionMode) {
    this.mode = mode;
    // If switching modes, quiet down previous inputs
    const quietData: MotionData = {
      x: 0, y: 0, z: 0, rotAlpha: 0, rotBeta: 0, rotGamma: 0, intensity: 0, jerk: 0
    };
    this.listeners.forEach(cb => cb(quietData));
  }

  public async checkAvailability(): Promise<{ supported: boolean; restricted: boolean }> {
    if (this.isDesktop) return { supported: true, restricted: false };

    const hasMotion = 'DeviceMotionEvent' in window;
    const hasOrientation = 'DeviceOrientationEvent' in window;

    if (!hasMotion && !hasOrientation) {
      return { supported: false, restricted: false };
    }

    if (typeof (DeviceMotionEvent as any).requestPermission === 'function') {
      return { supported: true, restricted: true };
    }

    return { supported: true, restricted: false };
  }

  public async requestPermission(): Promise<boolean> {
    if (this.isDesktop) return true;

    if (typeof (DeviceMotionEvent as any).requestPermission === 'function') {
      try {
        const motionState = await (DeviceMotionEvent as any).requestPermission();
        if (motionState !== 'granted') return false;
        if (typeof (DeviceOrientationEvent as any).requestPermission === 'function') {
          await (DeviceOrientationEvent as any).requestPermission();
        }
        return true;
      } catch (error) {
        console.error("Motion permission error:", error);
        return false;
      }
    }
    return true;
  }

  public start() {
    if (this.isListening) return;
    
    if (this.isDesktop) {
      window.addEventListener('mousemove', this.handleMouseMove);
    } else {
      window.addEventListener('devicemotion', this.handleMotion, true);
      window.addEventListener('deviceorientation', this.handleOrientation, true);
      // Global touch listeners removed to allow selective interaction in Touch Mode
    }
    this.isListening = true;
  }

  public stop() {
    if (this.isDesktop) {
      window.removeEventListener('mousemove', this.handleMouseMove);
    } else {
      window.removeEventListener('devicemotion', this.handleMotion);
      window.removeEventListener('deviceorientation', this.handleOrientation);
    }
    this.isListening = false;
  }

  public subscribe(callback: (data: MotionData) => void) {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter(l => l !== callback);
    };
  }

  private currentOrientation = { alpha: 0, beta: 0, gamma: 0 };

  private handleOrientation = (event: DeviceOrientationEvent) => {
    if (this.mode !== InteractionMode.MOTION) return;
    this.currentOrientation = {
      alpha: event.alpha || 0,
      beta: event.beta || 0,
      gamma: event.gamma || 0,
    };
  };

  private handleMouseMove = (event: MouseEvent) => {
    if (this.mode !== InteractionMode.MOTION) return;
    const rawGamma = (event.clientX / window.innerWidth) * 180 - 90;
    const rawBeta = (event.clientY / window.innerHeight) * 180 - 90;
    
    const gamma = Math.max(-90, Math.min(90, rawGamma * this.sensitivity));
    const beta = Math.max(-90, Math.min(90, rawBeta * this.sensitivity));

    const distFromCenterX = Math.abs((event.clientX / window.innerWidth) - 0.5) * 2;
    const distFromCenterY = Math.abs((event.clientY / window.innerHeight) - 0.5) * 2;
    const intensity = Math.min(Math.max(distFromCenterX, distFromCenterY) * 1.2 * this.sensitivity, 2.0);

    const movement = Math.hypot(event.movementX || 0, event.movementY || 0);
    const jerk = Math.min((movement / 10) * this.sensitivity, 4);

    const data: MotionData = {
      x: (event.clientX / window.innerWidth) * 2 - 1,
      y: (event.clientY / window.innerHeight) * 2 - 1,
      z: 0,
      rotAlpha: 0,
      rotBeta: beta,
      rotGamma: gamma,
      intensity: intensity,
      jerk: jerk
    };
    this.listeners.forEach(cb => cb(data));
  };

  /**
   * Process touch events from a specific play area.
   */
  public handleManualTouch = (touch: { clientX: number; clientY: number } | null, rect: DOMRect, type: 'start' | 'move' | 'end') => {
    if (this.mode !== InteractionMode.TOUCH) return;

    if (type === 'end' || !touch) {
      const quietData: MotionData = {
        x: 0, y: 0, z: 0, rotAlpha: 0, rotBeta: 0, rotGamma: 0, intensity: 0, jerk: 0
      };
      this.listeners.forEach(cb => cb(quietData));
      return;
    }

    const relX = (touch.clientX - rect.left) / rect.width;
    const relY = (touch.clientY - rect.top) / rect.height;

    const rawGamma = relX * 180 - 90;
    const rawBeta = relY * 180 - 90;

    const gamma = Math.max(-90, Math.min(90, rawGamma * this.sensitivity));
    const beta = Math.max(-90, Math.min(90, rawBeta * this.sensitivity));

    const distFromCenterX = Math.abs(relX - 0.5) * 2;
    const distFromCenterY = Math.abs(relY - 0.5) * 2;
    const intensity = Math.min(Math.max(distFromCenterX, distFromCenterY) * 1.5 * this.sensitivity, 2.0);

    const data: MotionData = {
      x: 0, y: 0, z: 0, rotAlpha: 0, rotBeta: beta, rotGamma: gamma, intensity: intensity, 
      jerk: type === 'start' ? 3.0 * this.sensitivity : 0
    };

    this.listeners.forEach(cb => cb(data));
  };

  private handleMotion = (event: DeviceMotionEvent) => {
    if (this.mode !== InteractionMode.MOTION) return;
    const { accelerationIncludingGravity } = event;
    if (!accelerationIncludingGravity) return;

    const x = accelerationIncludingGravity.x || 0;
    const y = accelerationIncludingGravity.y || 0;
    const z = accelerationIncludingGravity.z || 0;

    const jerk = (Math.abs(x - this.lastX) + Math.abs(y - this.lastY) + Math.abs(z - this.lastZ)) * this.sensitivity;
    const magnitude = Math.sqrt(x * x + y * y + z * z);
    const intensity = (Math.abs(magnitude - 9.8) / 9.8) * this.sensitivity;

    const beta = Math.max(-90, Math.min(90, this.currentOrientation.beta * this.sensitivity));
    const gamma = Math.max(-90, Math.min(90, this.currentOrientation.gamma * this.sensitivity));

    const data: MotionData = {
      x, y, z,
      rotAlpha: this.currentOrientation.alpha,
      rotBeta: beta,
      rotGamma: gamma,
      intensity: Math.min(intensity, 2.0),
      jerk: Math.min(jerk, 5.0),
    };

    this.lastX = x;
    this.lastY = y;
    this.lastZ = z;

    this.listeners.forEach(cb => cb(data));
  };
}

export const motionService = new MotionService();
